import { useEffect, useState } from "react";
import { useWalletConnection } from "@/hooks";
import { DEFAULT_CHAIN_ID, NETWORK_CONFIG } from "@/config";
import { Dialog } from "@/components";
import { Typography } from "@/components";
import { Button } from "@/components";
import { useWalletClient } from "wagmi";

interface EthereumError {
    code: number;
    message: string;
}

export function NetworkChecker() {
    const { isConnected } = useWalletConnection();
    const [showNetworkDialog, setShowNetworkDialog] = useState(false);
    const [isSwitching, setIsSwitching] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const { data: walletClient } = useWalletClient();

    useEffect(() => {
        if (isConnected && walletClient) {
            handleSwitchNetwork();
        }
    }, [isConnected, walletClient]);

    const handleSwitchNetwork = async () => {
        if (!walletClient || !isConnected) return;

        try {
            setIsSwitching(true);
            setError(null);

            // First try to switch network
            try {
                await walletClient.request({
                    method: "wallet_switchEthereumChain",
                    params: [{ chainId: `0x${DEFAULT_CHAIN_ID.toString(16)}` }],
                });
                setShowNetworkDialog(false);
                return;
            } catch (switchError: unknown) {
                const error = switchError as EthereumError;
                // If error code is 4902, we need to add the chain first
                if (error.code === 4902) {
                    const networkConfig = NETWORK_CONFIG[DEFAULT_CHAIN_ID];

                    // Request to add the chain
                    await walletClient.request({
                        method: "wallet_addEthereumChain",
                        params: [
                            {
                                chainId: `0x${DEFAULT_CHAIN_ID.toString(16)}`,
                                chainName: networkConfig.name,
                                nativeCurrency: {
                                    name: "ETH",
                                    symbol: "ETH",
                                    decimals: 18,
                                },
                                rpcUrls: [networkConfig.rpcUrl],
                                blockExplorerUrls: [networkConfig.blockExplorerUrl],
                            },
                        ],
                    });

                    // Try switching again after adding
                    await walletClient.request({
                        method: "wallet_switchEthereumChain",
                        params: [{ chainId: `0x${DEFAULT_CHAIN_ID.toString(16)}` }],
                    });
                    setShowNetworkDialog(false);
                    return;
                }
                throw error;
            }
        } catch (error: unknown) {
            const ethError = error as EthereumError;
            console.error("Failed to switch network:", error);
            setError(
                ethError.code === 4902
                    ? "Please add Arbitrum to your MetaMask and try again."
                    : "Failed to switch network. Please try switching manually in your wallet.",
            );
            setShowNetworkDialog(true);
        } finally {
            setIsSwitching(false);
        }
    };

    const handleCloseDialog = () => {
        setShowNetworkDialog(false);
        setError(null);
    };

    return (
        <Dialog open={showNetworkDialog} onClose={handleCloseDialog}>
            <div className="p-6 space-y-4">
                <Typography variant="h6">Switch Network Required</Typography>
                <Typography>This application requires you to be on the correct network.</Typography>
                {error && <Typography className="text-red-500">{error}</Typography>}
                <div className="flex justify-end gap-2">
                    <Button variant="secondary" onClick={handleCloseDialog}>
                        Cancel
                    </Button>
                    <Button onClick={handleSwitchNetwork} disabled={isSwitching}>
                        {isSwitching ? "Switching..." : "Switch Network"}
                    </Button>
                </div>
            </div>
        </Dialog>
    );
}
